import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Check, X, Loader2, Users, Clock, Crown, Mail, Flag, Music4, Ticket, FileUp, Gift, MailCheck, Palette, Trash2, Download, Inbox, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useAuth } from "@/context/AuthContext";
import {
  fetchPendingPosts, moderatePost, fetchAdminStats, resolveImage,
  fetchReports, resolveReport, publishSong,
  fetchInvites, createInvite, deactivateInvite,
  fetchStories, uploadStoryMedia,
  fetchPackSubscribers, fetchPackArt, uploadPackArt, deletePackArt,
  fetchContactMessages, fetchPackAnalytics,
  BACKEND_URL,
} from "@/lib/api";
import { PACK_DAYS } from "./packContent";

const SONG_CATEGORIES = ["Praise", "Bible Stories", "Bedtime", "Gratitude", "Movement"];

const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [songForm, setSongForm] = useState({ title: "", category: "Praise", lyric: "", verse: "" });
  const [songMsg, setSongMsg] = useState("");
  const [invites, setInvites] = useState([]);
  const [inviteForm, setInviteForm] = useState({ code: "", max_uses: 100 });
  const [inviteMsg, setInviteMsg] = useState("");
  const [stories, setStories] = useState([]);
  const [mediaMsg, setMediaMsg] = useState({});
  const [packSubs, setPackSubs] = useState([]);
  const [packArt, setPackArt] = useState({});
  const [artMsg, setArtMsg] = useState({});
  const [contactMsgs, setContactMsgs] = useState([]);
  const [packAnalytics, setPackAnalytics] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/signin");
    if (user && user.role !== "admin") navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.role === "admin") {
      fetchPendingPosts().then(setPending).catch(() => {});
      fetchAdminStats().then(setStats).catch(() => {});
      fetchReports().then(setReports).catch(() => {});
      fetchInvites().then(setInvites).catch(() => {});
      fetchStories().then(setStories).catch(() => {});
      fetchPackSubscribers().then(setPackSubs).catch(() => {});
      fetchPackArt().then(setPackArt).catch(() => {});
      fetchContactMessages().then(setContactMsgs).catch(() => {});
      fetchPackAnalytics().then(setPackAnalytics).catch(() => {});
    }
  }, [user]);

  if (!user || user.role !== "admin") {
    return (
      <div className="grid min-h-screen place-items-center bg-[#FFFDF6]">
        <Loader2 className="h-8 w-8 animate-spin text-[#FFC107]" />
      </div>
    );
  }

  const act = async (postId, action) => {
    await moderatePost(postId, action);
    setPending((ps) => ps.filter((p) => p.post_id !== postId));
    setStats((s) => s && {
      ...s,
      posts_pending: s.posts_pending - 1,
      posts_approved: action === "approve" ? s.posts_approved + 1 : s.posts_approved,
    });
  };

  const resolve = async (reportId, action) => {
    await resolveReport(reportId, action);
    setReports((rs) => rs.filter((r) => r.report_id !== reportId));
  };

  const publish = async (e) => {
    e.preventDefault();
    setSongMsg("");
    try {
      const song = await publishSong(songForm);
      setSongMsg(`"${song.title}" is live — every family with song alerts was notified.`);
      setSongForm({ title: "", category: "Praise", lyric: "", verse: "" });
    } catch (err) {
      setSongMsg(err.response?.data?.detail || "Could not publish that song.");
    }
  };

  const makeInvite = async (e) => {
    e.preventDefault();
    setInviteMsg("");
    try {
      const inv = await createInvite(inviteForm);
      setInvites((ls) => [inv, ...ls]);
      setInviteForm({ code: "", max_uses: 100 });
      setInviteMsg(`Invite "${inv.code}" created.`);
    } catch (err) {
      setInviteMsg(err.response?.data?.detail || "Could not create that invite.");
    }
  };

  const switchOff = async (code) => {
    await deactivateInvite(code);
    setInvites((ls) => ls.map((i) => (i.code === code ? { ...i, active: false } : i)));
  };

  const uploadMedia = async (storyId, file) => {
    if (!file) return;
    setMediaMsg((m) => ({ ...m, [storyId]: "Uploading…" }));
    try {
      const story = await uploadStoryMedia(storyId, file,
        (p) => setMediaMsg((m) => ({ ...m, [storyId]: `Uploading in safe chunks… ${p}%` })));
      setStories((ss) => ss.map((s) => (s.story_id === storyId ? story : s)));
      setMediaMsg((m) => ({ ...m, [storyId]: "Linked to the story page!" }));
    } catch (err) {
      setMediaMsg((m) => ({ ...m, [storyId]: err.response?.data?.detail || "Upload failed." }));
    }
  };

  const uploadArt = async (day, file) => {
    if (!file) return;
    setArtMsg((m) => ({ ...m, [day]: "Uploading…" }));
    try {
      const res = await uploadPackArt(day, file);
      setPackArt((a) => ({ ...a, [String(day)]: res.url }));
      setArtMsg((m) => ({ ...m, [day]: "Your artwork is live on the pack page!" }));
    } catch (err) {
      setArtMsg((m) => ({ ...m, [day]: err.response?.data?.detail || "Upload failed." }));
    }
  };

  const removeArt = async (day) => {
    await deletePackArt(day).catch(() => {});
    setPackArt((a) => {
      const next = { ...a };
      delete next[String(day)];
      return next;
    });
    setArtMsg((m) => ({ ...m, [day]: "Starter colouring sheet restored." }));
  };

  const statCards = [
    { icon: Users, label: "Parent accounts", value: stats?.users, color: "#0284C7" },
    { icon: Mail, label: "Waitlist families", value: stats?.waitlist, color: "#FFC107" },
    { icon: Clock, label: "Posts under review", value: stats?.posts_pending, color: "#FF6B6B" },
    { icon: Crown, label: "FAMILY+ members", value: stats?.family_plus, color: "#9333EA" },
    { icon: Gift, label: "Pack signups", value: stats?.pack_signups, color: "#16A34A" },
    { icon: MailCheck, label: "Pack list active", value: stats?.pack_active, color: "#0284C7" },
  ];

  return (
    <div className="min-h-screen bg-[#FFFDF6] pb-24 pt-28" data-testid="admin-page">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <Link to="/" className="text-sm font-bold text-slate-400 transition-colors hover:text-slate-700" data-testid="admin-back-link">
          &larr; Back to home
        </Link>
        <div className="mt-6 flex items-center gap-3">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-slate-900">
            <ShieldCheck className="h-6 w-6 text-[#FFC107]" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Moderation dashboard</h1>
            <p className="text-sm text-slate-500">Every community post passes through here.</p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-3" data-testid="admin-stats">
          {statCards.map((s) => (
            <div key={s.label} className="rounded-3xl border border-[#E2DDD0] bg-white p-5">
              <s.icon className="h-5 w-5" style={{ color: s.color }} />
              <p className="mt-3 font-display text-3xl font-bold text-slate-900">{s.value ?? "–"}</p>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>

        {packAnalytics && (
          <div className="mt-12 rounded-3xl border border-[#E2DDD0] bg-white p-6" data-testid="pack-growth-chart">
            <p className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
              <TrendingUp className="h-5 w-5 text-[#16A34A]" /> Pack signups — last 30 days
            </p>
            <div className="mt-4 h-44">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={packAnalytics.days} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} interval={4} axisLine={false} tickLine={false} />
                  <Tooltip
                    cursor={{ fill: "#F8F5EC" }}
                    contentStyle={{ borderRadius: 12, border: "1px solid #E2DDD0", fontSize: 12 }}
                    formatter={(v) => [`${v} signup${v === 1 ? "" : "s"}`, "Pack"]}
                  />
                  <Bar dataKey="signups" fill="#16A34A" radius={[6, 6, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
              <span className="rounded-full bg-[#DCFCE7] px-3 py-1 text-[#16A34A]" data-testid="pack-total-chip">{packAnalytics.total} total</span>
              <span className="rounded-full bg-[#E0F2FE] px-3 py-1 text-[#0284C7]" data-testid="pack-active-chip">{packAnalytics.active} active</span>
              <span className="rounded-full bg-[#FFE5E5] px-3 py-1 text-[#DC2626]" data-testid="pack-unsub-chip">{packAnalytics.unsubscribed} unsubscribed</span>
            </div>
          </div>
        )}

        <div className="mt-12 flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold text-slate-900">
            <Gift className="h-5 w-5 text-[#16A34A]" /> Pack subscribers
          </h2>
          <a
            href={`${BACKEND_URL}/api/pack/subscribers/export`}
            data-testid="pack-export-btn"
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition-transform hover:scale-[1.04]"
          >
            <Download className="h-4 w-4" /> Download spreadsheet
          </a>
        </div>
        <div className="mt-5 rounded-3xl border border-[#E2DDD0] bg-white p-6" data-testid="admin-pack-subs">
          <p className="text-sm text-slate-500">
            Families who grabbed the free 7-Day Little Faith Activity Pack — and how far through
            the 4-email welcome journey they are.
          </p>
          {packSubs.length === 0 ? (
            <p className="mt-4 rounded-2xl border border-dashed border-[#E2DDD0] p-8 text-center text-sm text-slate-400" data-testid="admin-pack-subs-empty">
              No pack signups yet — share littlesongsbigfaith.com/free-pack to get the word out.
            </p>
          ) : (
            <ul className="mt-4 space-y-2" data-testid="admin-pack-subs-list">
              {packSubs.map((s) => (
                <li key={s.email} className="flex flex-wrap items-center gap-3 rounded-2xl bg-[#F8F5EC] px-4 py-3" data-testid={`pack-sub-${s.email}`}>
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#16A34A] font-display text-sm font-bold text-white">
                    {(s.first_name || "?")[0].toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-display text-sm font-bold text-slate-900">{s.first_name}</p>
                    <p className="truncate text-xs text-slate-400">{s.email}</p>
                  </div>
                  <span className="text-xs font-bold text-slate-400">
                    joined {(s.created_at || "").slice(0, 10)}
                  </span>
                  <span className="rounded-full bg-[#E0F2FE] px-3 py-1 text-xs font-bold text-[#0284C7]">
                    {Math.min(s.seq_step || 1, 4)} of 4 emails
                  </span>
                  <span className="rounded-full bg-[#FEF3C7] px-3 py-1 text-xs font-bold text-[#B45309]" data-testid={`pack-opens-${s.email}`}>
                    {s.open_count || 0} opens
                  </span>
                  {s.unsubscribed ? (
                    <span className="ml-auto rounded-full bg-[#FFE5E5] px-3 py-1 text-xs font-bold text-[#DC2626]">Unsubscribed</span>
                  ) : (
                    <span className="ml-auto rounded-full bg-[#DCFCE7] px-3 py-1 text-xs font-bold text-[#16A34A]">Active</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <h2 className="mt-10 font-display text-xl font-bold text-slate-900">Posts awaiting review</h2>
        {pending.length === 0 ? (
          <p className="mt-4 rounded-3xl border border-dashed border-[#E2DDD0] bg-white p-10 text-center text-slate-500" data-testid="admin-empty-queue">
            All clear — nothing waiting for review.
          </p>
        ) : (
          <div className="mt-5 space-y-4" data-testid="admin-pending-list">
            {pending.map((p, i) => (
              <motion.article
                key={p.post_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="rounded-3xl border border-[#E2DDD0] bg-white p-6"
                data-testid={`admin-post-${p.post_id}`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="grid h-10 w-10 place-items-center rounded-full font-display text-sm font-bold text-white"
                    style={{ backgroundColor: p.avatar_color }}
                  >
                    {p.author[0]}
                  </span>
                  <div>
                    <p className="font-display text-sm font-semibold text-slate-900">{p.author}</p>
                    <p className="text-xs text-slate-400">{p.family}</p>
                  </div>
                </div>
                <p className="mt-3 text-base text-slate-700 leading-relaxed">{p.text}</p>
                {p.image && (
                  <img
                    src={resolveImage(p.image)}
                    alt="Pending upload"
                    className="mt-3 max-h-64 rounded-2xl object-cover"
                  />
                )}
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => act(p.post_id, "approve")}
                    data-testid={`admin-approve-${p.post_id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#16A34A] px-5 py-2.5 text-sm font-bold text-white transition-transform hover:scale-105"
                  >
                    <Check className="h-4 w-4" /> Approve
                  </button>
                  <button
                    onClick={() => act(p.post_id, "reject")}
                    data-testid={`admin-reject-${p.post_id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#FFE5E5] px-5 py-2.5 text-sm font-bold text-[#DC2626] transition-transform hover:scale-105"
                  >
                    <X className="h-4 w-4" /> Reject
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        )}

        <h2 className="mt-12 flex items-center gap-2 font-display text-xl font-bold text-slate-900">
          <Flag className="h-5 w-5 text-[#FF6B6B]" /> Reports
        </h2>
        {reports.length === 0 ? (
          <p className="mt-4 rounded-3xl border border-dashed border-[#E2DDD0] bg-white p-10 text-center text-slate-500" data-testid="admin-reports-empty">
            No open reports.
          </p>
        ) : (
          <div className="mt-5 space-y-4" data-testid="admin-reports-list">
            {reports.map((r) => (
              <div key={r.report_id} className="rounded-3xl border border-[#E2DDD0] bg-white p-6" data-testid={`admin-report-${r.report_id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-[#FFE5E5] px-3 py-1 text-xs font-bold capitalize text-[#DC2626]">
                    {r.target_type}
                  </span>
                  <p className="text-sm font-bold text-slate-800">{r.target_label || r.target_id}</p>
                  <p className="text-xs text-slate-400">reported by {r.reporter_name}</p>
                </div>
                <p className="mt-2 text-sm text-slate-600">{r.reason}</p>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => resolve(r.report_id, "remove")}
                    data-testid={`report-remove-${r.report_id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#DC2626] px-5 py-2.5 text-sm font-bold text-white transition-transform hover:scale-105"
                  >
                    <X className="h-4 w-4" /> Remove content
                  </button>
                  <button
                    onClick={() => resolve(r.report_id, "dismiss")}
                    data-testid={`report-dismiss-${r.report_id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#F8F5EC] px-5 py-2.5 text-sm font-bold text-slate-600 transition-transform hover:scale-105"
                  >
                    <Check className="h-4 w-4" /> Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <h2 className="mt-12 flex items-center gap-2 font-display text-xl font-bold text-slate-900">
          <Music4 className="h-5 w-5 text-[#0284C7]" /> Publish a new song
        </h2>
        <form onSubmit={publish} className="mt-5 grid gap-3 rounded-3xl border border-[#E2DDD0] bg-white p-6" data-testid="publish-song-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              required
              value={songForm.title}
              onChange={(e) => setSongForm((f) => ({ ...f, title: e.target.value }))}
              data-testid="publish-title-input"
              placeholder="Song title"
              className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 outline-none focus:border-[#FFC107] focus:bg-white"
            />
            <select
              value={songForm.category}
              onChange={(e) => setSongForm((f) => ({ ...f, category: e.target.value }))}
              data-testid="publish-category-select"
              className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 outline-none focus:border-[#FFC107] focus:bg-white"
            >
              {SONG_CATEGORIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>
          <input
            value={songForm.lyric}
            onChange={(e) => setSongForm((f) => ({ ...f, lyric: e.target.value }))}
            data-testid="publish-lyric-input"
            placeholder="Signature lyric (optional)"
            className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 outline-none focus:border-[#FFC107] focus:bg-white"
          />
          <input
            value={songForm.verse}
            onChange={(e) => setSongForm((f) => ({ ...f, verse: e.target.value }))}
            data-testid="publish-verse-input"
            placeholder="Memory verse (optional, e.g. Psalm 23:1)"
            className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 outline-none focus:border-[#FFC107] focus:bg-white"
          />
          <button
            type="submit"
            data-testid="publish-song-btn"
            className="mt-1 rounded-full bg-slate-900 py-3 font-display text-base font-semibold text-white transition-transform hover:scale-[1.01]"
          >
            Publish &amp; notify families
          </button>
          {songMsg && (
            <p className="rounded-2xl bg-[#DCFCE7] px-4 py-3 text-sm font-semibold text-[#16A34A]" data-testid="publish-song-msg">
              {songMsg}
            </p>
          )}
        </form>

        <h2 className="mt-12 flex items-center gap-2 font-display text-xl font-bold text-slate-900">
          <Ticket className="h-5 w-5 text-[#16A34A]" /> Invite codes
        </h2>
        <div className="mt-5 rounded-3xl border border-[#E2DDD0] bg-white p-6" data-testid="admin-invites">
          <p className="text-sm text-slate-500">
            Sign-up is open to everyone — codes are optional now, and simply tracked when families use one.
          </p>
          <form onSubmit={makeInvite} className="mt-4 flex flex-wrap gap-3" data-testid="invite-form">
            <input
              required
              minLength={4}
              value={inviteForm.code}
              onChange={(e) => setInviteForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              data-testid="invite-code-input"
              placeholder="NEWCODE"
              className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 font-bold uppercase tracking-widest outline-none focus:border-[#FFC107] focus:bg-white"
            />
            <input
              type="number"
              min={1}
              max={10000}
              value={inviteForm.max_uses}
              onChange={(e) => setInviteForm((f) => ({ ...f, max_uses: Number(e.target.value) }))}
              data-testid="invite-max-uses-input"
              className="w-28 rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 outline-none focus:border-[#FFC107] focus:bg-white"
            />
            <button
              type="submit"
              data-testid="invite-create-btn"
              className="rounded-full bg-slate-900 px-6 py-3 font-display text-sm font-semibold text-white transition-transform hover:scale-105"
            >
              Create invite
            </button>
          </form>
          {inviteMsg && (
            <p className="mt-3 rounded-2xl bg-[#DCFCE7] px-4 py-3 text-sm font-semibold text-[#16A34A]" data-testid="invite-msg">
              {inviteMsg}
            </p>
          )}
          <ul className="mt-4 space-y-2" data-testid="invite-list">
            {invites.map((i) => (
              <li key={i.code} className="flex flex-wrap items-center gap-3 rounded-2xl bg-[#F8F5EC] px-4 py-3" data-testid={`invite-row-${i.code}`}>
                <span className="font-display text-base font-bold tracking-widest text-slate-900">{i.code}</span>
                <span className="text-xs font-bold text-slate-400">
                  {i.used_count} / {i.max_uses} used
                </span>
                {i.active ? (
                  <span className="rounded-full bg-[#DCFCE7] px-3 py-1 text-xs font-bold text-[#16A34A]">Active</span>
                ) : (
                  <span className="rounded-full bg-[#FFE5E5] px-3 py-1 text-xs font-bold text-[#DC2626]">Off</span>
                )}
                {i.active && (
                  <button
                    onClick={() => switchOff(i.code)}
                    data-testid={`invite-deactivate-${i.code}`}
                    className="ml-auto rounded-full border border-[#E2DDD0] px-4 py-1.5 text-xs font-bold text-slate-500 hover:border-[#FF6B6B] hover:text-[#DC2626]"
                  >
                    Deactivate
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>

        <h2 className="mt-12 flex items-center gap-2 font-display text-xl font-bold text-slate-900">
          <FileUp className="h-5 w-5 text-[#9333EA]" /> Story episodes
        </h2>
        <div className="mt-5 rounded-3xl border border-[#E2DDD0] bg-white p-6" data-testid="admin-story-media">
          <p className="text-sm text-slate-500">
            Upload a real narration (MP3) or episode video (MP4) for a story — it replaces the
            teaser on the page instantly.
          </p>
          <div className="mt-4 space-y-3">
            {stories.map((s) => (
              <div key={s.story_id} className="flex flex-wrap items-center gap-3 rounded-2xl bg-[#F8F5EC] px-4 py-3" data-testid={`story-media-${s.story_id}`}>
                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: s.color }} />
                <span className="font-display text-sm font-bold text-slate-900">{s.title}</span>
                <span className="flex gap-1.5">
                  {s.audio_url && <span className="rounded-full bg-[#E0F2FE] px-2.5 py-0.5 text-xs font-bold text-[#0284C7]">audio ✓</span>}
                  {s.video_url && <span className="rounded-full bg-[#F3E8FF] px-2.5 py-0.5 text-xs font-bold text-[#9333EA]">video ✓</span>}
                </span>
                <label className="ml-auto cursor-pointer rounded-full bg-slate-900 px-4 py-2 text-xs font-bold text-white transition-transform hover:scale-105" data-testid={`story-upload-${s.story_id}`}>
                  Upload MP3 / MP4
                  <input
                    type="file"
                    accept="audio/mpeg,video/mp4"
                    className="hidden"
                    onChange={(e) => uploadMedia(s.story_id, e.target.files?.[0])}
                  />
                </label>
                {mediaMsg[s.story_id] && (
                  <span className="w-full text-xs font-semibold text-slate-500" data-testid={`story-media-msg-${s.story_id}`}>
                    {mediaMsg[s.story_id]}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <h2 className="mt-12 flex items-center gap-2 font-display text-xl font-bold text-slate-900">
          <Palette className="h-5 w-5 text-[#FF6B6B]" /> Pack colouring sheets
        </h2>
        <div className="mt-5 rounded-3xl border border-[#E2DDD0] bg-white p-6" data-testid="admin-pack-art">
          <p className="text-sm text-slate-500">
            Replace any starter colouring sheet with your own illustration (PNG, JPG, WebP or SVG,
            up to 8MB) — it updates the free pack page instantly.
          </p>
          <div className="mt-4 space-y-3">
            {PACK_DAYS.map((d) => (
              <div key={d.day} className="flex flex-wrap items-center gap-3 rounded-2xl bg-[#F8F5EC] px-4 py-3" data-testid={`pack-art-row-${d.day}`}>
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full font-display text-xs font-bold text-white" style={{ backgroundColor: d.color }}>
                  {d.day}
                </span>
                {packArt[String(d.day)] ? (
                  <img src={resolveImage(packArt[String(d.day)])} alt="" className="h-10 w-10 rounded-lg bg-white object-contain" />
                ) : (
                  <d.Art className="h-10 w-10" />
                )}
                <span className="font-display text-sm font-bold text-slate-900">{d.title}</span>
                {packArt[String(d.day)] ? (
                  <span className="rounded-full bg-[#DCFCE7] px-2.5 py-0.5 text-xs font-bold text-[#16A34A]">your artwork</span>
                ) : (
                  <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-bold text-slate-400">starter art</span>
                )}
                <label className="ml-auto cursor-pointer rounded-full bg-slate-900 px-4 py-2 text-xs font-bold text-white transition-transform hover:scale-105" data-testid={`pack-art-upload-${d.day}`}>
                  Upload image
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={(e) => uploadArt(d.day, e.target.files?.[0])}
                  />
                </label>
                {packArt[String(d.day)] && (
                  <button
                    onClick={() => removeArt(d.day)}
                    data-testid={`pack-art-remove-${d.day}`}
                    className="inline-flex items-center gap-1 rounded-full border border-[#E2DDD0] px-3 py-2 text-xs font-bold text-slate-500 hover:border-[#FF6B6B] hover:text-[#DC2626]"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                )}
                {artMsg[d.day] && (
                  <span className="w-full text-xs font-semibold text-slate-500" data-testid={`pack-art-msg-${d.day}`}>
                    {artMsg[d.day]}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <h2 className="mt-12 flex items-center gap-2 font-display text-xl font-bold text-slate-900">
          <Inbox className="h-5 w-5 text-[#0284C7]" /> Contact inbox
        </h2>
        <div className="mt-5 rounded-3xl border border-[#E2DDD0] bg-white p-6" data-testid="admin-contact-inbox">
          <p className="text-sm text-slate-500">
            Every message from the contact page — also emailed to you, but kept safe here.
          </p>
          {contactMsgs.length === 0 ? (
            <p className="mt-4 rounded-2xl border border-dashed border-[#E2DDD0] p-8 text-center text-sm text-slate-400" data-testid="contact-inbox-empty">
              No messages yet — the contact form lives at /contact.
            </p>
          ) : (
            <ul className="mt-4 space-y-3" data-testid="contact-inbox-list">
              {contactMsgs.map((m, i) => (
                <li key={`${m.email}-${m.created_at}`} className="rounded-2xl bg-[#F8F5EC] px-4 py-3" data-testid={`contact-msg-${i}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-sm font-bold text-slate-900">{m.name}</span>
                    <a href={`mailto:${m.email}`} className="text-xs font-bold text-[#0284C7] hover:underline" data-testid={`contact-reply-${i}`}>
                      {m.email}
                    </a>
                    <span className="ml-auto text-xs font-bold text-slate-400">
                      {(m.created_at || "").slice(0, 16).replace("T", " ")} UTC
                    </span>
                  </div>
                  <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-slate-600">{m.message}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
